import React, { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FileUp, Eye, ArrowLeft, Upload, UserCheck } from "lucide-react";

const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-3 py-2.5 text-sm";
const STATUS = { new: ["New", "#E3F1EC", "#0B5E4E"], update: ["Update", "#E6EEFB", "#1D4ED8"], skip: ["Skipped", "#F1F1EF", "#6B7280"] };
const FILTERS = [["all", "All"], ["new", "New"], ["update", "Updates"], ["skip", "Skipped"], ["issues", "Postcode issues"]];

const Chip = ({ id, label, value, tone }) => (
  <div data-testid={`cell-import-count-${id}`} className="rounded-xl px-3 py-2" style={{ background: tone[0] }}>
    <div className="text-xl font-extrabold font-head" style={{ color: tone[1] }}>{value}</div>
    <div className="text-[11px] font-medium" style={{ color: tone[1] }}>{label}</div>
  </div>
);

const PreviewTable = ({ rows }) => (
  <div className="overflow-auto max-h-[42vh] rounded-xl border" style={{ borderColor: "var(--sj-border)" }}>
    <table className="w-full min-w-[760px] text-sm">
      <thead className="sticky top-0" style={{ background: "#F7F6F3" }}>
        <tr className="text-left text-[11px] uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>
          <th className="px-3 py-2">Row</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Postcode</th><th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Member match</th><th className="px-3 py-2">Result</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const [label, bg, fg] = STATUS[r.status];
          return (
            <tr key={r.row} data-testid={`cell-import-row-${r.row}`} className="border-t" style={{ borderColor: "var(--sj-border)", opacity: r.status === "skip" ? 0.6 : 1 }}>
              <td className="px-3 py-2" style={{ color: "var(--sj-text-3)" }}>{r.row}</td>
              <td className="px-3 py-2"><div className="font-medium">{r.name || "—"}</div><div className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>{r.email || r.phone}</div></td>
              <td className="px-3 py-2">{r.postcode || "—"}{r.postcode_issue === "invalid" && <div className="text-[11px]" style={{ color: "#B42318" }}>Invalid postcode</div>}{r.postcode_issue === "extra_text" && <div className="text-[11px]" style={{ color: "#1D4ED8" }}>Will use {r.detected_postcode}</div>}</td>
              <td className="px-3 py-2 text-xs" style={{ color: "var(--sj-text-2)" }}>{r.timestamp || "—"}</td>
              <td className="px-3 py-2 text-xs">{r.matched_member ? <span className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--sj-primary)" }}><UserCheck className="h-3.5 w-3.5" /> {r.matched_member}</span> : <span style={{ color: "var(--sj-text-3)" }}>{r.status === "skip" ? "" : "No member match"}</span>}</td>
              <td className="px-3 py-2"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: bg, color: fg }}>{label}</span>{r.reason && <div className="text-[11px] mt-0.5" style={{ color: "var(--sj-text-3)" }}>{r.reason}</div>}{r.status === "update" && r.existing_source && <div className="text-[11px] mt-0.5" style={{ color: "var(--sj-text-3)" }}>Existing {r.existing_source === "portal" ? "portal signup" : r.existing_source === "form" ? "form response" : "import"}</div>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const CellImportDialog = ({ open, onOpenChange, onDone }) => {
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCsv(""); setFileName(""); setPreview(null); setFilter("all"); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCsv(String(reader.result || "")); setFileName(f.name); setPreview(null); };
    reader.readAsText(f);
    e.target.value = "";
  };
  const run = async (path) => {
    setBusy(true);
    try { return (await api.post(path, { csv })).data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };
  const doPreview = async () => { const d = await run("/cell/responses/import/preview"); if (d) setPreview(d); };
  const doImport = async () => {
    const d = await run("/cell/responses/import");
    if (!d) return;
    toast.success(`Imported: ${d.created} added, ${d.updated} updated${d.skipped ? `, ${d.skipped} skipped` : ""}`);
    close(false); onDone();
  };

  const c = preview?.counts;
  const shown = (preview?.rows || []).filter((r) => filter === "all" || (filter === "issues" ? r.postcode_issue && r.status !== "skip" : r.status === filter));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="cell-import-dialog" className={`w-[calc(100vw-1.5rem)] max-h-[92vh] overflow-y-auto overflow-x-hidden ${preview ? "max-w-5xl" : "max-w-xl"}`}>
        <DialogHeader><DialogTitle>{preview ? "Check before importing" : "Import cell group responses"}</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-3 min-w-0">
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>In Google Sheets open <b>Form Responses 1</b> → File → Download → CSV, then choose the file below (or paste its contents). You'll see a preview before anything is saved.</p>
            <label data-testid="cell-import-file-label" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer text-sm font-medium hover:bg-[#F7FAF8]" style={{ borderColor: "#B9D3C9", color: "var(--sj-primary)" }}>
              <FileUp className="h-5 w-5" /> {fileName || "Choose CSV file"}
              <input data-testid="cell-import-file" type="file" accept=".csv,text/csv" className="hidden" onChange={pickFile} />
            </label>
            <textarea data-testid="cell-import-textarea" value={csv} onChange={(e) => { setCsv(e.target.value); setFileName(""); }} rows={6} placeholder={"…or paste CSV here\nTimestamp,Full Name,Phone Number,Email Address,Postcode,…"} className={inputCls + " font-mono text-xs"} />
          </div>
        ) : (
          <div className="space-y-3 min-w-0" data-testid="cell-import-preview">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Chip id="new" label="New responses" value={c.new} tone={["#E3F1EC", "#0B5E4E"]} />
              <Chip id="update" label="Updates to existing" value={c.update} tone={["#E6EEFB", "#1D4ED8"]} />
              <Chip id="matched" label="Matched to members" value={c.matched_members} tone={["#FEF6D8", "#8a6a00"]} />
              <Chip id="duplicates" label="Older duplicates skipped" value={c.duplicates_in_file} tone={["#FBEEE6", "#B45309"]} />
              <Chip id="skip" label="Skipped in total" value={c.skip} tone={["#F1F1EF", "#4B5563"]} />
            </div>
            <div className="text-xs flex flex-wrap gap-1.5 items-center" data-testid="cell-import-columns" style={{ color: "var(--sj-text-3)" }}>
              <span className="font-semibold">Columns:</span>
              {preview.columns.map((col, i) => <span key={i} className="px-2 py-0.5 rounded-full max-w-full truncate" style={{ background: col.field ? "#E3F1EC" : "#F1F1EF", color: col.field ? "var(--sj-primary)" : "var(--sj-text-3)" }} title={col.header}>{col.field ? `${col.header.slice(0, 28)} → ${col.field}` : `${col.header.slice(0, 28)} (ignored)`}</span>)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map(([k, l]) => <button key={k} data-testid={`cell-import-filter-${k}`} onClick={() => setFilter(k)} className="rounded-full px-3 py-1 text-xs font-medium border" style={filter === k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>{l}</button>)}
            </div>
            <PreviewTable rows={shown} />
          </div>
        )}
        <DialogFooter className="gap-2">
          {preview ? (
            <>
              <button data-testid="cell-import-back" onClick={() => setPreview(null)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--sj-border)" }}><ArrowLeft className="h-4 w-4" /> Back</button>
              <button data-testid="cell-import-submit" disabled={busy || c.new + c.update === 0} onClick={doImport} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm text-white font-semibold disabled:opacity-50" style={{ background: "var(--sj-primary)" }}><Upload className="h-4 w-4" /> {busy ? "Importing…" : `Import ${c.new + c.update} rows`}</button>
            </>
          ) : (
            <>
              <button onClick={() => close(false)} data-testid="cell-import-cancel" className="rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
              <button data-testid="cell-import-preview-btn" disabled={busy || !csv.trim()} onClick={doPreview} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm text-white font-semibold disabled:opacity-50" style={{ background: "var(--sj-primary)" }}><Eye className="h-4 w-4" /> {busy ? "Checking…" : "Preview import"}</button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
